<template>
	<BaseLink v-if="link" :class="tagClasses" :link="link">
		<BaseSvgIcon v-if="iconName" :name="iconName" /> {{ text }}
	</BaseLink>
	<span v-else :class="tagClasses"> <BaseSvgIcon v-if="iconName" :name="iconName" /> {{ text }} </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Link, TagBackground, TagSize } from 'types/layout';

interface Props {
	text?: string;
	background?: TagBackground;
	iconName?: string;
	size?: TagSize;
	link?: Link;
}
const props = withDefaults(defineProps<Props>(), {
	text: undefined,
	background: 'default',
	iconName: undefined,
	size: 'sm',
	link: undefined
});

const tagClasses = computed((): string => {
	const classes = ['tag', `tag--${props.background}`];

	if (props.size) {
		classes.push(`tag--${props.size}`);
	}

	return classes.join(' ');
});
</script>

<style lang="scss">
@use '../../styles/constants/colors.scss' as *;
@use 'sass:map';

.tag {
	align-items: center;
	background-color: token('bg-secondary');
	border-radius: 4px;
	gap: 4px;
	color: token('content-primary');
	display: inline-flex;
	// body-sm
	font-family: 'Source Sans Pro', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;
	font-size: 0.875rem;
	line-height: 1.5;
	font-weight: normal;
	height: 28px;
	padding: 2px 8px;
	vertical-align: middle;
	white-space: nowrap;

	&[href] {
		&:hover,
		&:focus {
			color: initial;
			filter: brightness(95%);
		}
	}

	+ .tag {
		margin-left: 0.5rem;
	}

	&.tag--xs {
		// body-xs
		font-size: 0.75rem;
		height: 24px;
	}

	&.tag--md {
		height: 32px;
	}

	&.tag--blue {
		background-color: token('info-background');
	}
	&.tag--green {
		background-color: map.get($validation-colors, 'success-tint');
	}
	&.tag--red {
		background-color: map.get($validation-colors, 'error-tint');
	}
	&.tag--white {
		background-color: map.get($brand-colors, 'white');
	}
	&.tag--gold-dark {
		background-color: token('gold-dark');
	}
	&.tag--black {
		background-color: token('content-primary');
		color: map.get($brand-colors, 'white');

		&[href] {
			&:hover,
			&:focus {
				color: white;
				background-color: token('content-secondary');
			}
		}
	}
	&.tag--gold-light {
		background-color: token('gold-light');
	}
}
</style>
